import axios from "axios";
import { getStoredToken } from "../utils/tokenUtils";
import { injectClientIdIntoConfig } from "../utils/clientContext";
import { installApiLoadingInterceptors } from "../utils/apiLoading";
import {
  API_BASE_PREFIXED,
  addFallbackInterceptor,
} from "../config/api.config";

// Use prefixed URL (new standard)
const USER_SERVICE_URL = API_BASE_PREFIXED.user;

// Create axios instance for user service
const userServiceAPI = axios.create({
  baseURL: USER_SERVICE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add fallback interceptor for backward compatibility
addFallbackInterceptor(
  userServiceAPI,
  API_BASE_PREFIXED.user,
  process.env.REACT_APP_USER_SERVICE_URL || "http://localhost:3001",
);

// Add auth token to requests
userServiceAPI.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return injectClientIdIntoConfig(config);
  },
  (error) => Promise.reject(error),
);

installApiLoadingInterceptors(userServiceAPI);

/**
 * User service for managing user CRUD operations
 */
export const userService = {
  /**
   * Create a new user
   * @param {Object} userData - User data
   * @param {string} userData.full_name - Full name
   * @param {string} userData.email - Email
   * @param {string} userData.department - Department (QA, Product, Dev, Support)
   * @param {string} userData.role - Role (chief-admin, project_manager, test_manager, team_lead, qa_engineer)
   * @param {string} userData.client_id - Client ID (required for non-chief-admin)
   * @returns {Promise} Response with created user
   */
  createUser: async (userData) => {
    const response = await userServiceAPI.post(
      "/api/v1/users/create",
      userData,
    );
    return response.data;
  },

  /**
   * Update existing user
   * @param {string} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise} Response with updated user
   */
  updateUser: async (userId, userData) => {
    const response = await userServiceAPI.post("/api/v1/users/update", {
      user_id: userId,
      ...userData,
    });
    return response.data;
  },

  /**
   * Find users with filters and pagination
   * @param {Object} filters - Filter criteria
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise} Response with users list
   */
  findUsers: async (filters = {}, page = 1, limit = 10) => {
    const response = await userServiceAPI.post("/api/v1/users/find", {
      ...filters,
      page,
      limit,
    });
    return response.data;
  },

  /**
   * Delete user (soft delete)
   * Uses updateUser endpoint to set is_deleted flag
   */
  deleteUser: async (userId) => {
    // Use updateUser endpoint to soft delete
    const response = await userServiceAPI.post("/api/v1/users/update", {
      user_id: userId,
      is_deleted: true,
    });
    return response.data;
  },

  /**
   * Change user password (admin action)
   * @param {string} userId - User ID
   * @param {string} newPassword - New password
   * @returns {Promise} Response confirmation
   */
  changeUserPassword: async (userId, newPassword) => {
    const response = await userServiceAPI.post(
      "/api/v1/users/change-password",
      {
        user_id: userId,
        new_password: newPassword,
      },
    );
    return response.data;
  },

  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @returns {Promise} Response with user data
   */
  getUserById: async (userId) => {
    const response = await userServiceAPI.post("/api/v1/users/find", {
      user_id: userId,
    });
    // findUsers returns { success: true, data: { users: [...], count: 1 } }
    if (response.data.success && response.data.data?.users?.length > 0) {
      return {
        success: true,
        data: response.data.data.users[0],
      };
    }
    return {
      success: false,
      message: "User not found",
    };
  },

  /**
   * Export users data and send via email
   * @param {Object} filters - Filter criteria
   * @param {string} email - Recipient email address
   * @returns {Promise} Response confirmation
   */
  exportUsers: async (filters = {}, email) => {
    const response = await userServiceAPI.post("/api/v1/users/export", {
      filters,
      email,
    });
    return response.data;
  },

  /**
   * Get user profile
   * @returns {Promise} Response with user profile data
   */
  getProfile: async () => {
    const response = await userServiceAPI.post("/api/v1/users/profile");
    return response.data;
  },

  /**
   * Update theme preference
   * @param {string} themePreference - Theme preference: 'LIGHT', 'DARK', or 'SYSTEM'
   * @returns {Promise} Response confirmation
   */
  updateThemePreference: async (themePreference) => {
    const response = await userServiceAPI.post("/api/v1/users/update-theme", {
      theme_preference: themePreference,
    });
    return response.data;
  },

  /**
   * Get current user's theme (mode, type, config)
   * @returns {Promise<{ success, data: { themeMode, themeType, themeConfig } }>}
   */
  getTheme: async () => {
    const response = await userServiceAPI.get("/api/v1/users/theme");
    return response.data;
  },

  /**
   * Save current user's theme (per-user only)
   * @param {Object} payload - { themeMode?, themeType?, themeConfig? }
   * @returns {Promise}
   */
  saveTheme: async (payload) => {
    const response = await userServiceAPI.post("/api/v1/users/theme", payload);
    return response.data;
  },

  /**
   * Get users with filters (alias for findUsers for consistency)
   * @param {Object} filters - Filter criteria (client_id, is_active, is_deleted, etc.)
   * @returns {Promise} Response with users list
   */
  getUsers: async (filters = {}) => {
    const response = await userServiceAPI.post("/api/v1/users/find", {
      ...filters,
      page: 1,
      limit: 1000, // Get all users for selection
    });
    return response.data;
  },

  /**
   * Get client auth settings (SSO providers + 1FA/2FA)
   * @param {string} clientId - Client ID
   * @returns {Promise} Response with auth settings
   */
  getAuthSettings: async (clientId) => {
    const response = await userServiceAPI.get(
      `/api/clients/${clientId}/auth-settings`,
    );
    return response.data;
  },

  /**
   * Update client auth settings
   * @param {string} clientId - Client ID
   * @param {Object} settings - { googleEnabled, microsoftEnabled, oktaEnabled, samlEnabled, githubEnabled, oneFaEnabled, twoFaEnabled }
   * @returns {Promise} Response with updated settings
   */
  updateAuthSettings: async (clientId, settings) => {
    const response = await userServiceAPI.put(
      `/api/clients/${clientId}/auth-settings`,
      settings,
    );
    return response.data;
  },

  /**
   * Invite user: create pending user and send email with set-password link
   * @param {Object} payload - { full_name, email, department, role, client_id }
   * @returns {Promise} Response with created pending user
   */
  inviteUser: async (payload) => {
    const response = await userServiceAPI.post("/api/v1/users/invite", payload);
    return response.data;
  },

  /**
   * Get invite details by token (for accept-invite page)
   * @param {string} token - Invite token from email link
   * @returns {Promise} { success, data: { email, full_name } }
   */
  getInviteByToken: async (token) => {
    const response = await userServiceAPI.get(
      `/api/auth/invite/${encodeURIComponent(token)}`,
    );
    return response.data;
  },

  /**
   * Accept invite: set password for invited user
   * @param {string} token - Invite token
   * @param {string} newPassword - New password
   * @returns {Promise} Response
   */
  acceptInvite: async (token, newPassword) => {
    const response = await userServiceAPI.post("/api/auth/invite/accept", {
      token,
      new_password: newPassword,
    });
    return response.data;
  },
};

export default userService;
